import { ForwardedRef, forwardRef, ReactNode, useCallback, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { MenuItem } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/solid";
import _ from "lodash";

import { cx } from "utils";

import { StatefulComponentThemeProps, Theme, Themes } from "styles/themes";

import { useContactSales } from "hooks/useContactSales";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";

import { BillingPeriodUnit } from "types/BillingPeriodUnit";
import { PlanActions } from "types/billing/PlanActions";
import PlanCustomFields from "types/PlanCustomFields";

import Button from "components/Button";
import Menu, { MenuButton, MenuItems } from "components/Menu";
import PriceTag from "components/PriceTag";
import Spinner from "components/Spinner";
import { PureTooltip } from "components/Tooltip";

import PlanFeatures from "./planFeatures";


export interface PricingPlanProps {
  name: string;
  price: number | undefined;
  originalPrice?: number;
  period?: number;
  periodUnit?: BillingPeriodUnit;
  isAnnual?: boolean;
  customPlanOverride?: Partial<PlanCustomFields>;
  planSlug: string;
  theme: Theme;
  bannerLabel?: string;
  buttonAction: PlanActions | PlanActions[];
  buttonClassName?: string;
  buttonDisabled?: boolean;
  buttonTooltip?: JSX.Element | ReactNode | string;
  buttonTooltipClickable?: boolean;

  planAutoRenewal: number | undefined;

  planIdx: number;
  planInActionIdx: number | undefined;
  setPlanInActionIdx: (planIdx: number) => void;

  maxCreditsOfAvailablePlans?: number;
}

interface PricingPlanThemeType extends StatefulComponentThemeProps {
  bannerFill: string;
  bannerTextColor: string;
  borderColor: string;
}

const PricingPlanThemes: Themes<PricingPlanThemeType> = {
  primary: {
    default: {
      base: "",
      disabled: "bg-lightGray dark:bg-neutral-500 text-black border-borderColor dark:border-neutral-200",
      bannerFill: "bg-brandPrimary dark:bg-primary-600",
      bannerTextColor: "text-white",
      borderColor: "border-brandPrimary dark:border-primary-600"
    },
    destructive: {
      base: "",
      bannerFill: "bg-red-600 dark:bg-error-600",
      bannerTextColor: "text-white",
      borderColor: "border-red-600 dark:border-error-600"
    },
  },
  secondary: {
    default: {
      base: "",
      bannerFill: "bg-lightGray dark:bg-neutral-500",
      bannerTextColor: "text-white",
      borderColor: "border-borderColor dark:border-neutral-200"
    },
  },
  warning: {
    default: {
      base: "",
      bannerFill: "bg-warning-600",
      bannerTextColor: "text-white",
      borderColor: "border-warning-600"
    }
  },
  // disabled: {
  //   default: {
  //     base: "",
  //     bannerFill: "bg-neutral-500",
  //     bannerTextColor: "text-black",
  //     borderColor: "border-neutral-200"
  //   }
  // }
};


function ComponentWithTooltip(
  {
    tooltipContent,
    tooltipClickable,
    className,
    children
  }: {
    tooltipContent: string | JSX.Element | ReactNode | undefined;
    tooltipClickable?: boolean;
    className?: string;
    children: JSX.Element | ReactNode;
  }
) {
  if (tooltipContent) {
    return (
      <PureTooltip content={ tooltipContent } className={ className } clickable={ tooltipClickable }>
        { children }
      </PureTooltip>
    );
  } else {
    return <div className={ className }>{ children }</div>;
  }
}


const PricingPlan = forwardRef(
  ({
    name,
    price,
    originalPrice,
    period = 1,
    periodUnit = "month",
    isAnnual,
    customPlanOverride,
    planSlug,
    theme,
    bannerLabel,
    buttonAction,
    buttonClassName,
    buttonDisabled,
    buttonTooltip,
    buttonTooltipClickable,
    planAutoRenewal,
    planIdx,
    planInActionIdx,
    setPlanInActionIdx,
    maxCreditsOfAvailablePlans,
   }: PricingPlanProps,
   ref: ForwardedRef<HTMLDivElement>
  ) => {
    const location = useLocation();

    const isCheckingBillingInfo = useMemo(() => {
        return planIdx === planInActionIdx;
      },
      [ planIdx, planInActionIdx ]
    );

    const setCheckingBillingInfo = useCallback(() => {
        setPlanInActionIdx(planIdx);
      },
      [ planIdx, setPlanInActionIdx ]
    );

    const features = PlanFeatures(planSlug, customPlanOverride, maxCreditsOfAvailablePlans);

    const cardTheme = PricingPlanThemes[theme] || PricingPlanThemes['secondary'];

    const buttonProps = {
      className: buttonClassName || "button button-secondary",
      fullWidth: true,
      centerAlign: true,
      icon: isCheckingBillingInfo ? {
        Icon: Spinner,
        absolute: true
      } : undefined,
      disabled: buttonDisabled
    };

    if (planInActionIdx !== undefined) {
      buttonProps.disabled = true;
    }

    const { contactSalesFn } = useContactSales();

    const buttonActionsArray = _.flatten([ buttonAction ]);

    const hasButtonAction = (action: PlanActions) => {
      return _.includes(buttonActionsArray, action);
    };

    const { pricingOptions } = usePricingOptions();

    const ActionButton = useMemo(() => {
      if (buttonActionsArray[0] === "renew") {
        return <Button
          { ...buttonProps }
          text="Renew now"
          href={ `confirm-renewal/${ planSlug }` }
        />
      }

      if (buttonActionsArray[0] === "auto_upgrade") {
        return <Button
          { ...buttonProps }
          text={ (pricingOptions?.["auto-upgrade"]?.enabled ? "Disable" : "Enable") + " auto-upgrade" }
          href={ pricingOptions?.["auto-upgrade"]?.enabled ? "auto-upgrade/disable" : "auto-upgrade/enable" }
          shouldPassBackgroundLocation
        />
      }

      if (buttonActionsArray[0] === "pay_as_you_go") {
        return <Button
          { ...buttonProps }
          text={ (pricingOptions?.["pay-as-you-go"]?.enabled ? "Change" : "Enable") + " pay-as-you-go" }
          href={ pricingOptions?.["pay-as-you-go"]?.enabled ? "pay-as-you-go/change" : "pay-as-you-go/enable" }
          shouldPassBackgroundLocation
        />
      }

      return <></>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ buttonActionsArray, planSlug, pricingOptions ]);


    const renewalActions = (keyIndex: number) => {
      return [
        <MenuItem key={ "renewal-menu-item-0-" + keyIndex }>
          { ({ close }) => (
            <Link
              to={ `confirm-renewal/${ planSlug }` }
              onClick={ () => {
                setCheckingBillingInfo();
                close();
              } }
              state={ { backgroundLocation: location } }
              className="group !capitalize flex w-full items-center px-2 py-2 text-sm hover:bg-brandPrimary/50 dark:hover:bg-primary-600/50 hover:text-white text-gray-900 dark:text-neutral-900"
            >
              Renew now
            </Link>
          ) }
        </MenuItem>,

        <MenuItem key={ "renewal-menu-item-1-" + keyIndex }>
          <Link
            to="auto-renewal/95"
            onClick={ setCheckingBillingInfo }
            state={ { backgroundLocation: location } }
            className={ cx(
              (planAutoRenewal === 95) &&
              "!bg-brandPrimary !dark:bg-primary-600 !text-white pointer-events-none",
              "group !capitalize flex w-full items-center px-2 py-2 text-sm hover:bg-brandPrimary/50 dark:hover:bg-primary-600/50 hover:text-white text-gray-900 dark:text-neutral-900"
            ) }
          >
            Renew automatically at 95% usage
          </Link>
        </MenuItem>,

        <MenuItem key={ "renewal-menu-item-2-" + keyIndex }>
          <Link
            to="auto-renewal/100"
            onClick={ setCheckingBillingInfo }
            state={ { backgroundLocation: location } }
            className={ cx(
              (planAutoRenewal === 100) &&
              "!bg-brandPrimary !dark:bg-primary-600 !text-white pointer-events-none",
              "group !capitalize flex w-full items-center px-2 py-2 text-sm hover:bg-brandPrimary/50 dark:hover:bg-primary-600/50 hover:text-white text-gray-900 dark:text-neutral-900"
            ) }
          >
            Renew automatically at 100% usage
          </Link>
        </MenuItem>,

        <MenuItem key={ "renewal-menu-item-3-" + keyIndex }>
          <Link
            to="auto-renewal/clear"
            onClick={ setCheckingBillingInfo }
            state={ { backgroundLocation: location } }
            className={ cx(
              (planAutoRenewal === null) &&
              "!bg-brandPrimary !dark:bg-primary-600 !text-white pointer-events-none",
              "group !capitalize flex w-full items-center px-2 py-2 text-sm hover:bg-brandPrimary/50 dark:hover:bg-primary-600/50 hover:text-white text-gray-900 dark:text-neutral-900"
            ) }
          >
            Standard renewal cycle
          </Link>
        </MenuItem>
      ];
    }


    return (
      <div
        className="flex flex-col h-full w-full"
        ref={ ref }
      >
        <div
          className={ cx(!bannerLabel && "invisible", cardTheme?.default?.bannerFill, cardTheme?.default?.bannerTextColor, "text-center text-xs py-1") }>
          { bannerLabel || "placeholder" }
        </div>

        <div className={ cx(cardTheme?.default?.borderColor, "flex flex-col h-full px-5 py-6 border") }>
          <div className="text-sm uppercase text-brandDarkest dark:text-primary-800 font-medium">
            { name }
          </div>

          <div className={ "pt-1 pb-5 border-b border-borderColor dark:border-neutral-200" }>
            <PriceTag
              price={ price || "Custom" }
              originalPrice={ originalPrice }
              hasDiscount={ Boolean(price && originalPrice && (price !== originalPrice)) }
              period={ period }
              periodUnit={ periodUnit }
              isAnnual={ isAnnual || false }
            />
          </div>

          <ul className="flex flex-col pt-5 pb-6 space-y-4 text-xs">
            { features?.map((feature, idx) =>
              <li key={ idx } className="flex items-center">
                { feature }
              </li>
            ) }
          </ul>

          <div className="flex items-end mt-4 h-full">
            { buttonAction === "cancel_change" && (
              <Button
                { ...buttonProps }
                text="Cancel Change"
                href="cancel-subscription-change"
                shouldPassBackgroundLocation
                onClick={buttonDisabled ? undefined : setCheckingBillingInfo}
              />
            ) }

            { buttonAction === "contact_sales" && (
              <Button
                { ...buttonProps }
                text="Contact Sales"
                onClick={ contactSalesFn }
              />
            ) }

            { buttonAction === "downgrade" && (
              <Button
                { ...buttonProps }
                text="Downgrade"
                href={ `downgrade-subscription/${ planSlug }` }
                shouldPassBackgroundLocation
                onClick={ buttonDisabled ? undefined : setCheckingBillingInfo }
              />
            ) }

            { buttonAction === "downgrade_annual" && (
              <Button
                { ...buttonProps }
                text="Downgrade"
                href={ `downgrade-annual-subscription/${ planSlug }` }
                shouldPassBackgroundLocation
                onClick={ buttonDisabled ? undefined : setCheckingBillingInfo }
              />
            ) }

            { buttonAction === "downgrade_enterprise" && (
              <Button
                { ...buttonProps }
                text="Downgrade"
                href="cancel-enterprise"
                shouldPassBackgroundLocation
              />
            ) }

            { buttonAction === "pay_now" && (
              <Button
                { ...buttonProps }
                text="Pay Now"
                href="/pay-now"
                shouldPassBackgroundLocation
                onClick={ buttonDisabled ? undefined : setCheckingBillingInfo }
              />
            ) }

            { ((buttonAction === "renew") || (_.isEqual(buttonActionsArray, [ "renew" ]))) && (
              <ComponentWithTooltip tooltipContent={ buttonTooltip } tooltipClickable={ buttonTooltipClickable } className="relative w-full flex items-stretch flex-shrink-0 justify-center">
                <Button
                  { ...buttonProps }
                  text="Renew Now"
                  href={ `confirm-renewal/${ planSlug }` }
                  shouldPassBackgroundLocation
                  onClick={ buttonDisabled ? undefined : setCheckingBillingInfo }
                />
                { buttonDisabled && (
                  <div className="button button-primary disabled flex items-center px-3 !border-l-0">
                    <ChevronDownIcon
                      className="w-5 h-5 text-gray dark:text-neutral-600"
                      aria-hidden="true"
                    />
                  </div>
                ) }
                { !buttonDisabled && (
                  <Menu>
                    <MenuButton>
                      <div className="button button-primary h-full px-3 flex items-center">
                        <ChevronDownIcon
                          className="w-5 h-5 text-white group-hover:text-white"
                          aria-hidden="true"
                        />
                      </div>
                    </MenuButton>
                    <MenuItems
                      anchor={ { to: "bottom end", padding: "1rem", gap: "0.5rem" } }
                    >
                      { renewalActions(-1) }
                    </MenuItems>
                  </Menu>
                ) }
              </ComponentWithTooltip>
            ) }

            { (buttonActionsArray.length === 1) && (
              <>
                { hasButtonAction("pay_as_you_go") && ActionButton }

                { hasButtonAction("auto_upgrade") && ActionButton }
              </>
            ) }

            { buttonActionsArray.length > 1 && (
              // must be a combination of ("pay-as-you-go" or "auto-upgrade") and renewal
              <div className="relative w-full flex items-stretch flex-shrink-0 justify-center">
                { ActionButton }
                { buttonDisabled && (
                  <div className="button button-primary disabled flex items-center px-3 !border-l-0">
                    <ChevronDownIcon
                      className="w-5 h-5 text-gray dark:text-neutral-600"
                      aria-hidden="true"
                    />
                  </div>
                ) }
                { !buttonDisabled && (
                  <Menu>
                    <MenuButton>
                      { ({ open }) =>
                        <div className="button button-primary h-full px-3 flex items-center">
                          <ChevronDownIcon
                            className={ cx("w-5 h-5 text-white group-hover:text-white transition-transform", open && "rotate-180") }
                            aria-hidden="true"
                          />
                        </div>
                      }
                    </MenuButton>
                    <MenuItems
                      anchor={ { to: "bottom end", padding: "1rem", gap: "0.5rem" } }
                    >
                      { _.tail(buttonActionsArray).map((action, idx) => {
                        if (action === "renew") {
                          return renewalActions(idx);
                        }

                        if (action === "resubscribe") {
                          return (
                            <MenuItem key={ "menu-item-" + idx }>
                              { ({ close }) => (
                                <Link to={ `/confirm-resubscribe/${"yearly_" + planSlug.substring(7)}` }
                                      onClick={ () => {
                                        setCheckingBillingInfo();
                                        close();
                                      } }
                                      state={ { backgroundLocation: location } }
                                      className="group !capitalize flex w-full items-center px-2 py-2 text-sm hover:bg-brandPrimary/50 dark:hover:bg-primary-600/50 hover:text-white text-gray-900 dark:text-neutral-900"
                                >
                                  Resubscribe now
                                </Link>
                              ) }
                            </MenuItem>
                          )
                        }

                        return undefined;
                      }) }
                    </MenuItems>
                  </Menu>
                ) }
              </div>
            ) }

            { buttonAction === "upgrade" && (
              <Button
                { ...buttonProps }
                text="Upgrade"
                href={ `upgrade-subscription/${planSlug}` }
                shouldPassBackgroundLocation
                onClick={ buttonDisabled ? undefined : setCheckingBillingInfo }
              />
            ) }

            { buttonAction === "upgrade_annual" && (
              <Button
                { ...buttonProps }
                text="Upgrade"
                href={ `upgrade-annual-subscription/${ planSlug }` }
                shouldPassBackgroundLocation
              />
            ) }

          </div>
        </div>
      </div>
    );
  });

export default PricingPlan;
